import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/brand/Logo";
import { COMPANY, whatsappLink } from "@/data/constants";
import { LEGAL_ENTITY } from "@/data/legal";
import { useCookieConsent } from "@/hooks/useCookieConsent";

export const Footer = () => {
  const { openPreferences } = useCookieConsent();

  return (
    <footer className="mn-footer" data-testid="site-footer">
      <div className="mn-container py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Logo variant="light" />
          <p className="mt-6 text-sm text-white/65 leading-relaxed max-w-sm">
            Fornecedor de brindes corporativos personalizados — sacolas de algodão, sacolas TNT, squeezes e toalhas para empresas que querem ser lembradas com qualidade.
          </p>
          <div className="mt-7 flex items-center gap-3">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 inline-flex items-center justify-center border border-white/15 hover:border-[var(--mn-gold)] hover:text-[var(--mn-gold)] transition-colors rounded-sm"
              data-testid="footer-whatsapp"
              aria-label="WhatsApp"
            >
              <i className="fa-brands fa-whatsapp" />
            </a>
            <a
              href={COMPANY.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 inline-flex items-center justify-center border border-white/15 hover:border-[var(--mn-gold)] hover:text-[var(--mn-gold)] transition-colors rounded-sm"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <i className="fa-brands fa-instagram" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="font-serif-display font-bold text-base text-white mb-5">Produtos</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/produtos/ecobag-algodao">Sacolas de Algodão</Link></li>
            <li><Link to="/produtos/sacola-tnt">Sacolas TNT</Link></li>
            <li><Link to="/produtos/squeeze">Squeezes</Link></li>
            <li><Link to="/produtos/toalha-personalizada">Toalhas</Link></li>
            <li><a href="/#produtos">Brindes Corporativos</a></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="font-serif-display font-bold text-base text-white mb-5">Empresa</h4>
          <ul className="space-y-3 text-sm">
            <li><a href="/#diferenciais">Diferenciais</a></li>
            <li><a href="/#processo">Processo</a></li>
            <li><a href="/#galeria">Galeria</a></li>
            <li><Link to="/blog">Blog</Link></li>
            <li><a href="/#faq">FAQ</a></li>
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="font-serif-display font-bold text-base text-white mb-5">Contato</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <i className="fa-brands fa-whatsapp mt-0.5 text-[var(--mn-gold)]" />
              <div className="flex flex-col gap-1">
                <a href={`https://wa.me/${COMPANY.phonesRaw[0]}`} target="_blank" rel="noopener noreferrer">{COMPANY.phonesDisplay[0]}</a>
                <a href={`https://wa.me/${COMPANY.phonesRaw[1]}`} target="_blank" rel="noopener noreferrer">{COMPANY.phonesDisplay[1]}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-phone mt-0.5 text-[var(--mn-gold)]" />
              <a href={`tel:+${COMPANY.landlineRaw}`} data-testid="footer-landline">{COMPANY.landlineDisplay}</a>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-brands fa-instagram mt-0.5 text-[var(--mn-gold)]" />
              <a href={COMPANY.instagramUrl} target="_blank" rel="noopener noreferrer">{COMPANY.instagramHandle}</a>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-location-dot mt-0.5 text-[var(--mn-gold)]" />
              <span className="leading-relaxed">{COMPANY.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-credit-card mt-0.5 text-[var(--mn-gold)]" />
              <span>PIX · Cartão de Débito · Cartão de Crédito</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mn-container py-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="footer-heritage">
          <div className="flex items-center gap-5">
            <div className="bg-white/95 rounded-md p-3 flex items-center justify-center min-w-[80px]">
              <img
                src="/brand/roses-logo-160.png"
                alt="ROSE S Sacolas"
                data-testid="roses-logo-footer"
                className="h-10 w-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col text-white/80 leading-tight">
              <span className="font-serif-display font-bold text-base">M NAIEF Brindes Personalizados</span>
              <span className="text-xs text-white/60 mt-1">
                Empresa pertencente ao <strong className="text-[var(--mn-gold)]">Grupo ROSE S Sacolas</strong> · Mais de 20 anos de tradição
              </span>
            </div>
          </div>
          <a
            href="#sobre"
            className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[var(--mn-gold)] hover:text-white transition-colors whitespace-nowrap"
            data-testid="footer-about-link"
          >
            Conheça nossa história →
          </a>
        </div>
        <div className="mn-container py-6 flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-white/55 border-t border-white/10">
          <span>
            © {new Date().getFullYear()} {COMPANY.full}. Todos os direitos reservados. CNPJ{" "}
            {LEGAL_ENTITY.cnpj}.
          </span>
          <nav aria-label="Links legais" className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/politica-de-privacidade" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
              Política de Privacidade
            </Link>
            <Link to="/termos-de-uso" className="hover:text-white transition-colors" data-testid="footer-terms-link">
              Termos de Uso
            </Link>
            <Link to="/politica-de-cookies" className="hover:text-white transition-colors" data-testid="footer-cookies-link">
              Política de Cookies
            </Link>
            <button
              type="button"
              onClick={openPreferences}
              className="hover:text-white transition-colors underline underline-offset-2"
              data-testid="footer-cookie-preferences"
            >
              Preferências de cookies
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
